import { useEffect, useState, type JSX } from "react";
import FullScreenDialog, { FullScreenDialogCloseReason } from "../../components/full-screen-dialog/FullScreenDialog";
import { BindDialogReturn, DialogProps, DialogResult, DialogState, bindDialog, useDialogState } from "../../hooks/use-dialog-state/UseDialogState";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import SettingsBar, { SettingsActionEnum } from "../../components/settings-bar/SettingsBar";
import { useTranslation } from "react-i18next";
import { ContextActionMenu } from "../../components/contextual-app-bar/ContextActionMenu";
import { Alert, AutocompleteChangeDetails, AutocompleteChangeReason, Box, Button, MenuItem, Typography } from "@mui/material";
import { useObjectPropertyLists } from "../../queries/object-properties/UseObjectPropertyLists";
import { IeLynxObject } from "../../common/models/eLynxObject";
import Form from "../forms/form/Form";
import ModifyPollingScheduleUtils, { CUSTOM_SCHEDULE_ID, PollingScheduleForm, PollingScheduleFormListItem, defaultPollingScheduleForm } from "./ModifyPollingScheduleUtils";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { ContentSection } from "../content-section/ContentSection";
import OptionsSideSheet from "../options-side-sheet/OptionsSideSheet";
import { ContentContainer } from "../content-container/ContentContainer";
import SideSheetFilterInput from "../side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import { useModifyPollingScheduleState } from "./ModifyPollingScheduleDialogState";
import { IPollingSchedule, ITagPollingSchedule } from "../../services/properties-data-service/Models";
import FormPropertySection from "../forms/form-property-section/FormPropertySection";
import FormProperty from "../forms/form-property/FormProperty";
import FormSelect from "../forms/form-select/FormSelect";
import TagPollingScheduleList from "./TagPollingScheduleList";
import Enumerable from "linq";
import _ from "lodash";
import { getObjectTypeName } from "../../common/utilities/GetObjectTypeName";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { PollingScheduleEnum } from "../../services/polling-frequency-data-service/Models";
import { useSaveObjectPollingFrequencyData } from "../../queries/polling-frequency-admin/UseSaveObjectPollingFrequencyData";
import LoadingZone from "../loading-zone/LoadingZone";
import SideSheetMultiSelect from "../side-sheet/side-sheet-multiselect/SideSheetMultiSelect";


export type ModifyPollingScheduleDialogInitValues = {
    elynxObject: IeLynxObject,
    tagPollingSchedules: IPollingSchedule[]
};

export type ModifyPollingScheduleDialogResultValues = {
    newScheduleID: number;
};

export type ModifyPollingScheduleDialogResultReason = FullScreenDialogCloseReason | "cancel" | "save";

export type ModifyPollingScheduleDialogResult = DialogResult<ModifyPollingScheduleDialogResultValues, ModifyPollingScheduleDialogResultReason>;

export type ModifyPollingScheduleDialogProps = DialogProps<ModifyPollingScheduleDialogInitValues, ModifyPollingScheduleDialogResult>;

export default function ModifyPollingScheduleDialog({
    open,
    onClose,
    values
}: ModifyPollingScheduleDialogProps): JSX.Element {
    
    const [updating, setUpdating] = useState(false);
    const routeContext = useRouteContext();
    const { t } = useTranslation();

    const { confirm
        , acknowledge 
    } = useConfirmation();

    const { data: referenceData, status } = useQueryNotifications(useObjectPropertyLists)({
        groupID: routeContext.groupID as number,
        eLynxObject: routeContext.contextObject as IeLynxObject
    });

    const { mutate: savePollingSchedules } = useQueryNotifications(useSaveObjectPollingFrequencyData)({
        groupID: routeContext.groupID as number,
        eLynxObject: routeContext.contextObject as IeLynxObject,
        onSuccess: async (data) => {

            acknowledge(t("pollingFrequencyAdmin.dataSavedMessage"));

            setUpdating(false);

            onClose({
                reason: 'save',
                values: {
                    newScheduleID: scheduleID
                }
            });
        },
        onError: () => setUpdating(false)
    });

    const {
        state, 
        setFilter,
        setSelectedPollingSchedules,
        resetState
    } = useModifyPollingScheduleState();

    const form = useForm<PollingScheduleForm>({
        mode: 'onChange',
        defaultValues: {...defaultPollingScheduleForm
        }
    });

    const {
        getValues,
        setValue,
        watch,
        formState: {
            //dirtyFields,
            isDirty,
            //isValid,
            //defaultValues
            //touchedFields
        },
        reset,
        //setValue,
        control,
        handleSubmit,
        //trigger,
    } = form;

    const [
        scheduleID,
        pollingSchedules
    ] = watch([
        'scheduleID',
        'pollingSchedules'
    ]);

    const {
        //fields,
        //insert,
        //append,
        //remove:
        update,
        replace
    } = useFieldArray({
        control: control,
        name: 'pollingSchedules',
        keyName: "fieldKey"
    });    

    const pollingScheduleFormData = useWatch({ control: control }) as PollingScheduleForm;

    function onSetFilter(value: string) {
        setFilter(value);
    }

    function onScheduleChanged(value: number) {
        
        const items = ModifyPollingScheduleUtils.filterItems(_.cloneDeep(pollingSchedules), state.filterText, state.selectedPollingSchedules);

        if (state.filterText.trim() === '' && state.selectedPollingSchedules.some(o => o.selected && o.id === -1)) {
            items.forEach(o => o.scheduleID = value);
            replace(items);
        } else {
            items.forEach(o => {
                const ndx = pollingSchedules.findIndex(v => v.tagID === o.tagID);
                update(ndx, {...o,
                    scheduleID: value
                });
            });

            setValue('scheduleID', CUSTOM_SCHEDULE_ID, {shouldDirty: true});
        }
    }

    function onTagScheduleChanged (item: PollingScheduleFormListItem, value: number) {

        const currentScheduleID = getValues('scheduleID');

        const scheduleIDs =  Enumerable.from(pollingSchedules)
            .select(o => o.scheduleID)
            .distinct()
            .toArray();


        let updatedScheduleID = scheduleIDs.length > 1 ?
            CUSTOM_SCHEDULE_ID : 
            value;
        

        if (currentScheduleID !== updatedScheduleID) {

            setValue('scheduleID', updatedScheduleID, {shouldDirty: true});

        }
        
    }

    async function onOkay(_formValues: PollingScheduleForm) {

        const values = getValues();

        if (values.pollingSchedules.some(o => o.scheduleID === PollingScheduleEnum.OneMinute || 
            o.scheduleID === PollingScheduleEnum.TenSecond ||
            o.scheduleID === PollingScheduleEnum.FiveMinute)) {
                const result = await confirm({
                    dialogTitle: t("pollingFrequencyAdmin.highFrequencyWarning.title"),
                    confirmationText: t("pollingFrequencyAdmin.highFrequencyWarning.text"),
                    yesButtonText: t("buttons.yesContinue"),
                    noButtonText: t("buttons.noCancel"),
                    showConfirmationText: true,
                    showYesButton: true,
                    showNoButton: true,
                    showCloseButton: false
                });
                
            if (result !== "yes"){
                return;
            }
        }

        const model = ModifyPollingScheduleUtils.mapToModel(values);
        savePollingSchedules(model);

        setUpdating(true);        
    }

    async function onCancel() {

        const canCancel = isDirty
            ? await getUserConfirmation()
            : true;

        if (canCancel) {
            onClose && onClose({ 
                values: {
                    newScheduleID: -1,
                },
                reason: "cancel" 
            });
        }
    }


    async function getUserConfirmation() {

        const ok = (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";

        if (ok) {
            reset();
        }
        return ok;
    }

    function onFilterPollingSchedulesChanged(
        event: React.SyntheticEvent,
        value: ITagPollingSchedule[],
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<ITagPollingSchedule> | undefined
    ) {
        
        const items = _.cloneDeep(state.selectedPollingSchedules);

        let item = items.find(o => o.id === details?.option.id);

        if (item) {

            switch (reason) {
                case 'selectOption':
                    item.selected = true;

                    if (item.id === -1) {
                        items.forEach(o => {
                            if (o.id !== item?.id) {
                                o.selected = false;
                            }
                        });
                    } else {

                        items.filter(o => o.id === -1)
                            .forEach(o => o.selected = false);
                    }

                    break;

                case 'removeOption':
                    item.selected = false;

                    if (!items.find(o => o.selected)) {
                        item = items.find(o => o.id === -1);
                        if (item) {
                            item.selected = true;
                        }
                    }
                    break;

                default:
                    break;
            }
        }

        setSelectedPollingSchedules(items);
    }


    
    useEffect(() => {
        if (open && values?.elynxObject && values?.tagPollingSchedules && referenceData) {

            const formData = ModifyPollingScheduleUtils.mapToFormValues(values.elynxObject, 
                values.tagPollingSchedules, 
                referenceData);

            reset(formData);
        }
    },[
        values?.elynxObject,
        values?.tagPollingSchedules,
        referenceData,
        reset,
        open
    ]);


    useEffect(() => {

        const tagSchedules = _.cloneDeep(referenceData?.tagPollingSchedules ?? []);
        const items = [{
            id: -1,
            description: 'All Schedules',
            selected: true,
        }, ...tagSchedules];

        setSelectedPollingSchedules(items);
    }, [
        referenceData,
        setSelectedPollingSchedules
    ]);

    return (
        <FullScreenDialog open={open} onClose={reason => onClose({ reason: reason })}>
            <SettingsBar title={t('actions.modifyPollingSchedule')} 
                onClose={
                    async (reason: SettingsActionEnum) => {
                        if (reason === SettingsActionEnum.Close) {
                            onCancel();
                        }
                    }}
                
                hideAction={true}
                actions={
                    <Box
                        sx={{
                            display: 'inline-flex',
                            color: '#fff'
                        }}>

                        <ContextActionMenu 
                            viewOptionState={state.viewOptionState}
                            //onExecuteAction={onExecuteAction}
                            //actions={actions} 
                            hideAddToFavoritesAction={true} 
                            buttonColor="inherit"
                            />
                        <Button autoFocus 
                            color= {isDirty ? "secondary" : 'inherit' }
                            arial-label='save' 
                            disabled={!isDirty || updating} 
                            sx={{
                                color: isDirty ? undefined : 'rgba(255,255,255,.4)!important',
                                opacity: isDirty ? undefined : '.6!important',
                                marginLeft: 2
                            }}
                            onClick={() => handleSubmit(async (data) => onOkay(data))()}>
                            {t('buttons.save')}
                        </Button>                                                

                    </Box>}
            />
            <ContentContainer>

                <ContentSection>

                    <LoadingZone isLoading={status === 'loading' || updating}>

                        <Alert severity="info"
                            sx={{
                                margin: 1,
                                padding: 2
                        }}>
                            {t('pollingFrequencyAdmin.modifyInstructions', {objectType: getObjectTypeName(routeContext.contextObject?.objectType, false, true)})}
                        </Alert>


                        {
                        pollingScheduleFormData.pollingSchedules.length > 0 && 

                        <Form form={form} 
                            sx={{ padding: 0,
                                paddingTop: 1,
                                paddingLeft: 1
                            }}
                            >

                            <FormPropertySection label=''>
                                <FormProperty>
                                    <FormSelect
                                        name="scheduleID"
                                        label={t('pollingFrequencyAdmin.objectInterval', {objectType: getObjectTypeName(routeContext.contextObject?.objectType, false)})}
                                        sx={{ width: "100%" }}
                                        onChange={(e) => onScheduleChanged(+e.target.value)}
                                    >
                                        <MenuItem value={CUSTOM_SCHEDULE_ID} disabled={true}>
                                            {'Custom Per Tag'}
                                        </MenuItem>
                                        
                                        {referenceData?.tagPollingSchedules?.map((value, index) =>
                                            <MenuItem key={index} value={value.id}>
                                                {value.description}
                                            </MenuItem>
                                        )}

                                    </FormSelect>

                                </FormProperty>
                                
                            </FormPropertySection>


                            <Typography variant="subtitle1" sx={{
                                fontWeight: 600
                            }} >
                                {getObjectTypeName(eLynxObjectTypeEnum.Tag, true)}
                            </Typography>

                            <TagPollingScheduleList 
                                filterText={state.filterText} 
                                filteredSchedules={state.selectedPollingSchedules}
                                onChanged={onTagScheduleChanged}
                            />
                        </Form>
                        }

                    </LoadingZone>

                </ContentSection>

                <OptionsSideSheet onReset={resetState}>

                    <SideSheetFilterInput
                        label={t("pollingFrequencyAdmin.filterBy", {name: 'name'})}
                        value={state.filterText}
                        onChange={(value: string) => {
                            onSetFilter(value);
                        }}
                    />


                    <SideSheetMultiSelect<ITagPollingSchedule>
                        label={t('objectTagValues.filterPollingSchedules')}
                        // placeholder={configurationAllowed
                        //                                         ? t('objectTagValues.tagGroups')
                        //                                         : t('category.placeholder')
                        //                                     }
                        options={state.selectedPollingSchedules}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={state.selectedPollingSchedules.filter(o => o.selected)} //{state.categories.filter(o => o.selected)}
                        getOptionLabel={(option) => option.description}
                        onChange={onFilterPollingSchedulesChanged}
                    />

                </OptionsSideSheet>

            </ContentContainer>
        </FullScreenDialog>
    );
}

export type ModifyPollingScheduleDialogState = DialogState<ModifyPollingScheduleDialogInitValues, ModifyPollingScheduleDialogResult>;
export type BindModifyPollingScheduleDialogReturn = BindDialogReturn<ModifyPollingScheduleDialogInitValues, ModifyPollingScheduleDialogResult>;

export function useModifyPollingScheduleDialogState(): ModifyPollingScheduleDialogState {
    return useDialogState();
}

export function bindModifyPollingScheduleDialog(props: ModifyPollingScheduleDialogState): BindModifyPollingScheduleDialogReturn {
    return bindDialog(props);
}
